import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import copyToClipboard from 'copy-to-clipboard';
import useBranch from 'client/hooks/useBranch';

import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import { SR_CHECKBOX_THEME_BOX } from 'client/components/Checkbox';
import SrText from 'client/components/SrText';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import UpSelfOnboardingHeader from 'client/components/UPSelfOnboarding/UpSelfOnboardingHeader';

import './UpSelfOnboarding.less';

const instructions = [
  'Approval Email',
  'Tenant Welcome Email',
  'Reminder Emails',
  'Automated Email System',
  'Other',
];

const getTemplateText = ({ slug, utilityAppUrl }) =>
  'Important: Utility Setup Required\n' +
  'Before handing over the keys to your property, you must set up utilities in your name. ' +
  'To facilitate a smooth transition, please follow these steps:\n\n' +
  '1.   Access Our Platform: Use the following link to locate and connect with ' +
  `the required utility providers: ${utilityAppUrl}/${slug}\n` +
  '2.   Input Confirmation Details: Once you’ve established your utility services, input the ' +
  'relevant account or confirmation IDs into our platform. This step ensures we’re ' +
  'able to track and manage your utility services effectively.\n\n' +
  'Should you have any inquiries or require assistance regarding essential utility options, ' +
  'our chat support feature within the provided link is readily available to assist you.';

const getFormFields = () => {
  const formFields = [
    {
      id: 'instructions',
      title: 'Which email will you add it to?',
      type: 'multiple_choice',
      properties: {
        choices: instructions.map((action) => ({
          value: action,
          label: action,
        })),
        multipleSelection: true,
        fieldType: SR_CHECKBOX_THEME_BOX,
        checkboxClassName: 'selfOnboardingCheckbox',
        autoFitWidth: '100%',
      },
      validations: ['required', 'nonEmptyArray::Please select at least one option.'],
    },
  ];
  return formFields;
};

const UpSelfOnboardingEmailStep = ({
  utilityPartner,
  utilityPartnerGroup,
  activeIndex,
  onNext,
}) => {
  const { utilityAppUrl } = useBranch({ utilityAppUrl: ['env', 'UTILITY_APP_URL'] });
  const formFields = getFormFields();
  const [isClicked, setIsClicked] = useState(false);

  const theme = utilityPartnerGroup?.defaultTheme || {};

  const onCopyInstructions = () => {
    copyToClipboard(getTemplateText({ slug: utilityPartner.slug, utilityAppUrl }));
    setIsClicked(true);

    setTimeout(() => {
      setIsClicked(false);
    }, 2000);
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: formFields,
      })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onNext}
      render={(formikBag) => {
        return (
          <div styleName="layout">
            <div styleName="left">
              <UpSelfOnboardingHeader
                activeIndex={activeIndex}
                partnerGroupLogoUrl={utilityPartnerGroup?.logoUrl}
                partnerGroupPrimaryColor={theme.primaryColor}
                partnerGroupSecondaryColor={theme.secondaryColor}
                partnerGroupInactiveColor={theme.inactiveTextColor}
              />
              <div styleName="lineSeparator" />
              <Form styleName="form">
                <span styleName="titleText">Update your "tenant welcome email."</span>
                <div>
                  <SrBox marginTop="20px">
                    <SrText font="greycliff" weight="800" size="21">
                      Copy & paste into your templates:
                    </SrText>
                    <br />
                    <SrText font="greycliff" weight="500" paddingTop="7px">
                      You can get this later, we just emailed it to you.
                    </SrText>
                  </SrBox>
                  <div styleName="copyPasteBox" onClick={onCopyInstructions}>
                    <div styleName="templateBox">
                      <span>
                        Important: Utility Setup Required
                        <br />
                        Before handing over the keys to your property, you must set up utilities in
                        your name. To facilitate a smooth transition, please follow these steps:
                        <br />
                        <br />
                        1. Access Our Platform: Use the following link to locate and connect with
                        the required utility providers: {utilityAppUrl}/
                        {utilityPartner.slug}
                        <br />
                        2. Input Confirmation Details: Once you’ve established your utility
                        services, input the relevant account or confirmation IDs into our platform.
                        This step ensures we’re able to track and manage your utility services
                        effectively.
                        <br />
                        <br />
                        Should you have any inquiries or require assistance regarding essential
                        utility options, our chat support feature within the provided link is
                        readily available to assist you.
                      </span>
                    </div>
                    <div styleName="copyInstructions">
                      <span>{isClicked ? 'Copied!' : 'Click here to copy'}</span>
                    </div>
                  </div>
                </div>
                <SrBox margin="26px 0">
                  {formFields.map((formField, index) => (
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                      margin={index === 0 ? '0' : null}
                    />
                  ))}
                </SrBox>
                <SrBox marginTop="26px">
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    hasBoxShadow
                    type="submit"
                    themeOverrides={{
                      backgroundColor: theme?.buttonPrimaryColor,
                      color: theme?.buttonPrimaryTextColor,
                    }}
                  >
                    Continue
                  </SrButton>
                </SrBox>
              </Form>
            </div>
            <div styleName="right">
              <div styleName="content emailStep fadeIn">
                <div styleName="row">
                  <div styleName="imagesBox">
                    <img
                      src="/images/arrow-email-template.png"
                      alt="Template"
                      styleName="arrowImg"
                    />
                    <div styleName="row">
                      <span styleName="titleText">Example of "tenant welcome email":</span>
                      <img
                        src="/images/up-self-onboarding-welcome-email-template.png"
                        alt="Template"
                        styleName="emailImg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

UpSelfOnboardingEmailStep.displayName = 'UpSelfOnboardingEmailStep';

UpSelfOnboardingEmailStep.propTypes = {
  activeIndex: PropTypes.number.isRequired,
  utilityPartner: PropTypes.object.isRequired,
  utilityPartnerGroup: PropTypes.object,
  onNext: PropTypes.func.isRequired,
};

export default UpSelfOnboardingEmailStep;
